"use client";

/**
 * A store-based selection system that allows for fine-grained subscriptions.
 * Individual components can subscribe to their specific selection state
 * without causing re-renders of other components.
 */

export type SelectionType = "kitCanvas" | "assetLibrary" | "tagLibrary" | "portal" | "dashboard";

export type SelectionUpdateInput = {
  type: SelectionType;
  ids?: string[];
};

type SelectionSets = {
  kitCanvas: Set<string>;
  assetLibrary: Set<string>;
  tagLibrary: Set<string>;
  portal: Set<string>;
  dashboard: Set<string>;
};

// Cached arrays for useSyncExternalStore compatibility
type SelectionArrays = {
  kitCanvas: string[];
  assetLibrary: string[];
  tagLibrary: string[];
  portal: string[];
  dashboard: string[];
};

type Listener = () => void;
type ItemListener = {
  type: SelectionType;
  itemId: string;
  wasSelected: boolean;
  callback: Listener;
};

class SelectionStore {
  private selections: SelectionSets = {
    kitCanvas: new Set(),
    assetLibrary: new Set(),
    tagLibrary: new Set(),
    portal: new Set(),
    dashboard: new Set(),
  };

  // Cached arrays - only recreated when selection changes
  private cachedArrays: SelectionArrays = {
    kitCanvas: [],
    assetLibrary: [],
    tagLibrary: [],
    portal: [],
    dashboard: [],
  };

  // Global listeners for the entire selection state
  private globalListeners: Set<Listener> = new Set();

  // Per-item listeners for fine-grained updates
  private itemListeners: Map<string, ItemListener> = new Map();

  // Version number to track changes for snapshot stability
  private version = 0;

  // Edit handlers for different selection types (e.g., for focusing/editing items)
  private editHandlers: Map<SelectionType, (itemId: string) => void> = new Map();

  private updateCachedArray(type: SelectionType) {
    this.cachedArrays[type] = Array.from(this.selections[type]);
    this.version++;
  }

  private notifyGlobal() {
    this.globalListeners.forEach(listener => listener());
  }

  private notifyItemListeners(type: SelectionType, changedIds: Set<string>) {
    this.itemListeners.forEach(listener => {
      if (listener.type === type && changedIds.has(listener.itemId)) {
        const isNowSelected = this.selections[type].has(listener.itemId);
        // Only notify if selection state actually changed
        if (listener.wasSelected !== isNowSelected) {
          listener.wasSelected = isNowSelected;
          listener.callback();
        }
      }
    });
  }

  // Subscribe to the entire selection state for a type
  subscribeGlobal(listener: Listener): () => void {
    this.globalListeners.add(listener);
    return () => {
      this.globalListeners.delete(listener);
    };
  }

  // Subscribe to a specific item's selection state
  subscribeItem(type: SelectionType, itemId: string, callback: Listener): () => void {
    const key = `${type}:${itemId}`;
    const listener: ItemListener = {
      type,
      itemId,
      wasSelected: this.selections[type].has(itemId),
      callback,
    };
    this.itemListeners.set(key, listener);
    return () => {
      this.itemListeners.delete(key);
    };
  }

  // Get current selection as array (cached for reference stability)
  getSelection(type: SelectionType): string[] {
    return this.cachedArrays[type];
  }

  // Get current selection as Set (for efficient lookups)
  getSelectionSet(type: SelectionType): Set<string> {
    return this.selections[type];
  }

  // Check if a specific item is selected (O(1) lookup)
  isSelected(type: SelectionType, itemId: string): boolean {
    return this.selections[type].has(itemId);
  }

  // Get version for snapshot comparison
  getVersion(): number {
    return this.version;
  }

  // Select items (replaces current selection)
  selectItems({ type, ids }: SelectionUpdateInput): void {
    if (!ids) return;

    const currentSet = this.selections[type];
    const newSet = new Set(ids);

    // Find changed IDs
    const changedIds = new Set<string>();
    currentSet.forEach(id => {
      if (!newSet.has(id)) changedIds.add(id);
    });
    newSet.forEach(id => {
      if (!currentSet.has(id)) changedIds.add(id);
    });

    if (changedIds.size === 0) return;

    this.selections[type] = newSet;
    this.updateCachedArray(type);
    this.notifyGlobal();
    this.notifyItemListeners(type, changedIds);
  }

  // Add items to selection
  addItems({ type, ids }: SelectionUpdateInput): void {
    if (!ids || ids.length === 0) return;

    const currentSet = this.selections[type];
    const changedIds = new Set<string>();

    ids.forEach(id => {
      if (!currentSet.has(id)) {
        currentSet.add(id);
        changedIds.add(id);
      }
    });

    if (changedIds.size === 0) return;

    this.updateCachedArray(type);
    this.notifyGlobal();
    this.notifyItemListeners(type, changedIds);
  }

  // Remove items from selection
  deselectItems({ type, ids }: SelectionUpdateInput): void {
    if (!ids || ids.length === 0) return;

    const currentSet = this.selections[type];
    const changedIds = new Set<string>();

    ids.forEach(id => {
      if (currentSet.has(id)) {
        currentSet.delete(id);
        changedIds.add(id);
      }
    });

    if (changedIds.size === 0) return;

    this.updateCachedArray(type);
    this.notifyGlobal();
    this.notifyItemListeners(type, changedIds);
  }

  // Clear all selection for a type
  clearSelection({ type }: SelectionUpdateInput): void {
    const currentSet = this.selections[type];
    if (currentSet.size === 0) return;

    const changedIds = new Set(currentSet);
    this.selections[type] = new Set();

    this.updateCachedArray(type);
    this.notifyGlobal();
    this.notifyItemListeners(type, changedIds);
  }

  // Register an edit handler for a selection type (e.g., to focus/edit items in a gallery)
  registerEditHandler(type: SelectionType, handler: (itemId: string) => void): void {
    this.editHandlers.set(type, handler);
  }

  // Get the edit handler for a selection type
  getEditHandler(type: SelectionType): ((itemId: string) => void) | undefined {
    return this.editHandlers.get(type);
  }

  // Unregister an edit handler for a selection type
  unregisterEditHandler(type: SelectionType): void {
    this.editHandlers.delete(type);
  }
}

// Singleton instance
export const selectionStore = new SelectionStore();
